import React from 'react';
import '../../scss/controls/_checkbutton.scss';

/** Weight of the button's label text. */
export enum CheckButtonFontStyle {
    /** Regular (400) */
    Small = 'fontSmall',
    /** Medium (500) */
    Medium = 'fontMedium',
    /** SemiBold (600) */
    Large = 'fontLarge',
}

/** How the check button is drawn. */
export enum CheckButtonStyle {
    /** A rounded, filled button with the checkbox inside (the default; the original look). */
    Button = 'button',
    /**
     * Just the checkbox and its label, with no button around it (like a Condensed SelectionPanelGroup item), and the
     * same vertical spacing as the form inputs so it lines up beside them.
     */
    Plain = 'plain',
}

type CheckButtonProps = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    selected: boolean;
    disabled?: boolean;
    onToggle: () => void;
    width?: string;
    /** Fixed height (e.g. '48px'). When omitted, the button fills its parent's height as before. */
    height?: string;
    hideCheckbox?: boolean;
    anchorCheckboxTop?: boolean;
    /** Weight of the label text. Defaults to Medium. */
    fontStyle?: CheckButtonFontStyle;
    /** Button (default) or Plain. */
    checkButtonStyle?: CheckButtonStyle;
};

export const CheckButton: React.FC<CheckButtonProps> = ({
    id,
    label,
    icon,
    selected,
    onToggle,
    width = 'auto',
    height,
    disabled = false,
    hideCheckbox = false,
    anchorCheckboxTop = false,
    fontStyle = CheckButtonFontStyle.Medium,
    checkButtonStyle = CheckButtonStyle.Button,
}) => {
    // Button adds no class, so the original look is unchanged
    const styleClass = checkButtonStyle === CheckButtonStyle.Plain ? ' plain' : '';

    return (
        <label
            className={`labelButton tertiary ${fontStyle} ${selected ? 'is-checked' : ''} ${disabled ? 'disabled' : 'active'}${height ? ' fixedHeight' : ''}${styleClass}`}
            htmlFor={id}
            onClick={(e) => e.stopPropagation()}
            style={height ? { width, height } : { width }}
        >
            <div className={`checkbox-button-content ${anchorCheckboxTop ? 'checkbox-top' : ''}`}>
                <input
                    id={id}
                    type='checkbox'
                    checked={selected}
                    onChange={onToggle}
                    disabled={disabled}
                    className={hideCheckbox ? 'checkbox-native-hidden' : 'checkbox-native'}
                />
                <span className='label'>{label}</span>
                {icon && <div className='right-icon'>{icon}</div>}
            </div>
        </label>
    );
};
